import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { usePrefersReducedMotion } from "./reducedMotion";

/**
 * Sweeps the accent colour across a word once it reaches the middle of the
 * viewport; already swept under reduced motion. `dark` sets the pre-sweep
 * colour for a light word on the dark slab (the default) or ink on paper.
 */
const SweepWord = ({ children, dark = true }) => {
	const ref = useRef(null);
	const reduced = usePrefersReducedMotion();
	const [fired, setFired] = useState(reduced);

	useEffect(() => {
		if (!ref.current || fired) return undefined;
		const o = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) setFired(true);
			},
			{ rootMargin: "-20% 0px -20% 0px", threshold: 0 },
		);
		o.observe(ref.current);
		return () => o.disconnect();
	}, [fired]);

	const baseColor = dark ? "var(--pp-bg)" : "var(--pp-ink)";

	return (
		<span
			ref={ref}
			style={{
				backgroundImage: `linear-gradient(to right, var(--pp-accent) 50%, ${baseColor} 50%)`,
				backgroundSize: "200% 100%",
				backgroundPosition: fired ? "0% 0" : "100% 0",
				WebkitBackgroundClip: "text",
				WebkitTextFillColor: "transparent",
				backgroundClip: "text",
				display: "inline",
				transition: fired ? "background-position 0.8s cubic-bezier(0.22,1,0.36,1)" : "none",
			}}
		>
			{children}
		</span>
	);
};

SweepWord.propTypes = {
	children: PropTypes.node.isRequired,
	dark: PropTypes.bool,
};

export default SweepWord;
